import { useRouter } from '@tanstack/react-router';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderTitle,
  useModals,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useNeedsEmailConfirmation } from '@/features/push-credentials';
import { DocumentTitle } from '@/components/page/document-title';
import { ErrorPage } from '@/components/page/error';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import type { App } from '@/services/db/app/entity';
import { useApps } from '@/services/db/app/queries';
import { AppsTable } from './components/apps-table';
import { TestAppModal } from './components/test-app-modal';
import { useAppActions } from './hooks/use-app-actions';
import { useAppsBreadcrumbs } from './hooks/use-apps-breadcrumbs';

const PAGE_DESCRIPTION =
  'Register each iOS or Android app in your workspace. Each app has its own credentials, identifier, and asset library used in push notifications and live notifications.';

const CREATE_PERMISSION = 'journeys/push_message_settings/create';
const EDIT_PERMISSION = 'journeys/push_message_settings/edit';
const DELETE_PERMISSION = 'journeys/push_message_settings/delete';

type AppsIndexPageProps = {
  workspaceId: number;
};

/**
 * The error frame replaces the table only when the read has produced no rows,
 * so a failed re-read after a delete or a default change leaves the list standing.
 */
export default function AppsIndexPage({ workspaceId }: AppsIndexPageProps) {
  const router = useRouter();
  const modals = useModals();
  const { hasPermission } = usePermissions();
  const appsQuery = useApps();
  const isAccountUnconfirmed = useNeedsEmailConfirmation();

  useAppsBreadcrumbs(workspaceId);

  const apps = appsQuery.data ?? [];
  const hasApps = apps.length > 0;
  const actions = useAppActions({ workspaceId, apps });

  const createHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/actions/apps/create',
    params: { workspaceId },
  }).href;
  const getEditHref = (app: App) =>
    router.buildLocation({
      to: '/workspaces/$workspaceId/settings/actions/apps/edit/$appId',
      params: { workspaceId, appId: String(app.id) },
    }).href;

  const canCreate = hasPermission(CREATE_PERMISSION) === true;
  const canEdit = hasPermission(EDIT_PERMISSION) === true;
  const canDelete = hasPermission(DELETE_PERMISSION) === true;

  return (
    <>
      <DocumentTitle title="Mobile apps" />
      <Page>
        <PageHeader>
          <PageHeaderTitle>Mobile apps</PageHeaderTitle>
          <PageHeaderDescription>{PAGE_DESCRIPTION}</PageHeaderDescription>
          {hasApps ? (
            <PageHeaderActions>
              <Button
                variant="secondary"
                onClick={() => modals.open(TestAppModal, { workspaceId, apps, isAccountUnconfirmed })}
              >
                Test app…
              </Button>
              <AddAppButton href={createHref} canCreate={canCreate} />
            </PageHeaderActions>
          ) : null}
        </PageHeader>
        <PageBody>
          {appsQuery.isError && appsQuery.data == null ? (
            <ErrorPage error={appsQuery.error} />
          ) : hasApps || appsQuery.isPending ? (
            <AppsTable
              apps={apps}
              isLoading={appsQuery.isPending}
              canEdit={canEdit}
              canDelete={canDelete}
              isDeleting={actions.isDeleting}
              getEditHref={getEditHref}
              onSetDefault={(app) => void actions.requestSetDefault(app)}
              onDelete={(app) => void actions.requestDelete(app)}
            />
          ) : (
            <EmptyState>
              <EmptyStateTitle>No apps yet.</EmptyStateTitle>
              <EmptyStateDescription>
                Add your first mobile app to start sending push and live notifications.
              </EmptyStateDescription>
              <EmptyStateActions>
                <AddAppButton href={createHref} canCreate={canCreate} />
              </EmptyStateActions>
            </EmptyState>
          )}
        </PageBody>
      </Page>
    </>
  );
}

type AddAppButtonProps = {
  href: string;
  canCreate: boolean;
};

function AddAppButton({ href, canCreate }: AddAppButtonProps) {
  return (
    <ViewerRestrictedTooltip readOnly={!canCreate}>
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="primary"
          isDisabled={!canCreate}
          href={canCreate ? href : undefined}
        >
          Add app
        </Button>
      )}
    </ViewerRestrictedTooltip>
  );
}
